import React, { useEffect, useMemo, useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Select from "@mui/material/Select";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import firebase from "../../firebase";
import { boolToString, makeid } from "../../utility";
import SurveyFileComponent from "./SurveyFileComponent";

const answerFormats = [
    "Short Text",
    "Long Text",
    "Number",
    "True or False",
    "Checkboxes",
    "Multiple Choice",
    "Date",
];

export default function SurveyQuestionEdit({
    surveyKey,
    question,
    index = 0,
    displayNumber = 1,
    questionCount = 1,
    handleDeleteQuestionClick,
    handleQuestionMoveUpPressed,
    handleQuestionMoveDownPressed,
}) {
    const [questionText, setQuestionText] = useState("");
    const [answerFormat, setAnswerFormat] = useState("Short Text");
    const [answerRequired, setAnswerRequired] = useState(false);
    const [multipleChoiceOptions, setMultipleChoiceOptions] = useState([]);

    useEffect(() => {
        setQuestionText(question.questionText || "");
        setAnswerFormat(question.answerFormat || "Short Text");
        setAnswerRequired(Boolean(question.answerRequired));
        setMultipleChoiceOptions(question.multipleChoiceOptions || []);
    }, [question]);

    const sortedOptions = useMemo(
        () =>
            [...multipleChoiceOptions].sort(
                (first, second) => first.index - second.index,
            ),
        [multipleChoiceOptions],
    );

    function questionRef() {
        return firebase
            .database()
            .ref(`surveys/${surveyKey}/questions/${question.key}`);
    }

    function handleQuestionTextChange(event) {
        const newValue = event.target.value;
        setQuestionText(newValue);
        questionRef().child("questionText").set(newValue);
    }

    function handleOptionChange(optionKey, newValue) {
        setMultipleChoiceOptions((currentOptions) =>
            currentOptions.map((option) =>
                option.key === optionKey
                    ? { ...option, text: newValue }
                    : option,
            ),
        );
        questionRef()
            .child("multipleChoiceOptions")
            .child(optionKey)
            .child("text")
            .set(newValue);
    }

    function handleAddOptionPress() {
        const newOption = {
            index: multipleChoiceOptions.length,
            text: `Option ${multipleChoiceOptions.length + 1}`,
            key: makeid(20),
        };

        setMultipleChoiceOptions((currentOptions) => [
            ...currentOptions,
            newOption,
        ]);
        questionRef()
            .child("multipleChoiceOptions")
            .child(newOption.key)
            .set(newOption);
    }

    function handleDeleteOptionPress(optionKey) {
        questionRef()
            .child("multipleChoiceOptions")
            .child(optionKey)
            .remove();

        const updatedOptions = sortedOptions
            .filter((option) => option.key !== optionKey)
            .map((option, optionIndex) => ({
                ...option,
                index: optionIndex,
            }));

        updatedOptions.forEach((option) => {
            questionRef()
                .child("multipleChoiceOptions")
                .child(option.key)
                .set(option);
        });
        setMultipleChoiceOptions(updatedOptions);
    }

    function handleAnswerRequiredToggled(event) {
        const newValue = event.target.checked;
        setAnswerRequired(newValue);
        questionRef().child("answerRequired").set(boolToString(newValue));
    }

    function handleChangeAnswerFormat(event) {
        const newValue = event.target.value;
        setAnswerFormat(newValue);
        questionRef().child("answerFormat").set(newValue);
    }

    const isFileQuestion = answerFormat === "File";
    const answerFormatLabel = isFileQuestion ? "Linked resource" : answerFormat;
    const formatLabelId = `answer-format-label-${question.key}`;

    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: 3,
                overflow: "hidden",
                borderColor: "divider",
                backgroundColor: "background.paper",
                boxShadow: "0 5px 18px rgba(20, 35, 25, 0.055)",
            }}
        >
            <Box
                sx={{
                    minHeight: 64,
                    px: { xs: 1.5, sm: 2.5 },
                    py: 1.25,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.25,
                    backgroundColor: "background.subtle",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                }}
            >
                <Box
                    sx={{
                        width: 38,
                        height: 38,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        borderRadius: 1.75,
                        color: "primary.main",
                        backgroundColor: "rgba(0, 149, 61, 0.09)",
                    }}
                >
                    {isFileQuestion ? (
                        <InsertDriveFileOutlinedIcon fontSize="small" />
                    ) : (
                        <HelpOutlineIcon fontSize="small" />
                    )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        {isFileQuestion
                            ? `Resource ${displayNumber}`
                            : `Question ${displayNumber}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {answerFormatLabel}
                    </Typography>
                </Box>
                {!isFileQuestion && (
                    <Chip
                        size="small"
                        label={answerRequired ? "Required" : "Optional"}
                        sx={{
                            display: { xs: "none", sm: "flex" },
                            backgroundColor: answerRequired
                                ? "rgba(0, 149, 61, 0.09)"
                                : "action.hover",
                            color: answerRequired
                                ? "primary.dark"
                                : "text.secondary",
                        }}
                    />
                )}
                <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Tooltip title="Move up">
                        <span>
                            <IconButton
                                size="small"
                                aria-label={`Move ${
                                    isFileQuestion ? "resource" : "question"
                                } ${displayNumber} up`}
                                onClick={() =>
                                    handleQuestionMoveUpPressed(question)
                                }
                                disabled={index === 0}
                            >
                                <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Move down">
                        <span>
                            <IconButton
                                size="small"
                                aria-label={`Move ${
                                    isFileQuestion ? "resource" : "question"
                                } ${displayNumber} down`}
                                onClick={() =>
                                    handleQuestionMoveDownPressed(question)
                                }
                                disabled={index === questionCount - 1}
                            >
                                <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton
                            size="small"
                            color="error"
                            aria-label={`Delete ${
                                isFileQuestion ? "resource" : "question"
                            } ${displayNumber}`}
                            onClick={() => handleDeleteQuestionClick(question)}
                        >
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {isFileQuestion ? (
                <Box sx={{ p: { xs: 1.5, sm: 2.5 }, overflow: "hidden" }}>
                    <SurveyFileComponent
                        surveyKey={surveyKey}
                        isEditing={false}
                        question={question}
                    />
                </Box>
            ) : (
                <CardContent
                    sx={{
                        p: { xs: 2, sm: 2.5 },
                        "&:last-child": { pb: { xs: 2, sm: 2.5 } },
                    }}
                >
                    <TextField
                        fullWidth
                        value={questionText}
                        onChange={handleQuestionTextChange}
                        id={`question-text-${question.key}`}
                        label="Question"
                        placeholder="What would you like to ask?"
                    />

                    <Box
                        sx={{
                            mt: 2.5,
                            display: "grid",
                            gridTemplateColumns: {
                                xs: "minmax(0, 1fr)",
                                md: "220px minmax(0, 1fr)",
                            },
                            alignItems: "start",
                            gap: 2,
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5,
                            }}
                        >
                            <FormControl fullWidth>
                                <InputLabel id={formatLabelId}>
                                    Answer format
                                </InputLabel>
                                <Select
                                    labelId={formatLabelId}
                                    id={`answer-format-${question.key}`}
                                    value={answerFormat}
                                    onChange={handleChangeAnswerFormat}
                                    label="Answer format"
                                >
                                    {answerFormats.map((format) => (
                                        <MenuItem key={format} value={format}>
                                            {format}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Box
                                sx={{
                                    px: 1.5,
                                    py: 1,
                                    borderRadius: 2,
                                    border: "1px solid",
                                    borderColor: "divider",
                                }}
                            >
                                <FormControlLabel
                                    sx={{ m: 0 }}
                                    control={
                                        <Switch
                                            checked={answerRequired}
                                            onChange={
                                                handleAnswerRequiredToggled
                                            }
                                            color="primary"
                                        />
                                    }
                                    label="Answer required"
                                />
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                minWidth: 0,
                                p: { xs: 1.5, sm: 2 },
                                borderRadius: 2.5,
                                border: "1px solid",
                                borderColor: "divider",
                                backgroundColor: "background.subtle",
                            }}
                        >
                            <Typography
                                variant="overline"
                                color="text.secondary"
                                sx={{
                                    display: "block",
                                    mb: 1.25,
                                    fontWeight: 800,
                                    letterSpacing: "0.08em",
                                }}
                            >
                                Answer preview
                            </Typography>

                            {answerFormat === "Short Text" && (
                                <TextField
                                    fullWidth
                                    disabled
                                    label="Short text response"
                                />
                            )}

                            {answerFormat === "Long Text" && (
                                <TextField
                                    fullWidth
                                    disabled
                                    multiline
                                    minRows={3}
                                    label="Long text response"
                                />
                            )}

                            {answerFormat === "Number" && (
                                <TextField
                                    fullWidth
                                    disabled
                                    type="number"
                                    label="Number response"
                                />
                            )}

                            {answerFormat === "Date" && (
                                <TextField
                                    fullWidth
                                    disabled
                                    type="date"
                                    label="Date response"
                                    InputLabelProps={{ shrink: true }}
                                />
                            )}

                            {answerFormat === "True or False" && (
                                <RadioGroup aria-label="True or false preview">
                                    <FormControlLabel
                                        value="true"
                                        disabled
                                        control={<Radio />}
                                        label="True"
                                    />
                                    <FormControlLabel
                                        value="false"
                                        disabled
                                        control={<Radio />}
                                        label="False"
                                    />
                                </RadioGroup>
                            )}

                            {(answerFormat === "Checkboxes" ||
                                answerFormat === "Multiple Choice") && (
                                <Box>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 1,
                                        }}
                                    >
                                        {sortedOptions.map(
                                            (option, optionIndex) => (
                                                <Box
                                                    key={option.key}
                                                    sx={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: 0.75,
                                                    }}
                                                >
                                                    {answerFormat ===
                                                    "Checkboxes" ? (
                                                        <Checkbox
                                                            disabled
                                                            sx={{
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                    ) : (
                                                        <Radio
                                                            disabled
                                                            sx={{
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                    )}
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        value={option.text}
                                                        onChange={(event) =>
                                                            handleOptionChange(
                                                                option.key,
                                                                event.target
                                                                    .value,
                                                            )
                                                        }
                                                        inputProps={{
                                                            "aria-label": `Option ${
                                                                optionIndex + 1
                                                            }`,
                                                        }}
                                                    />
                                                    <Tooltip title="Delete option">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            aria-label={`Delete option ${
                                                                optionIndex + 1
                                                            }`}
                                                            onClick={() =>
                                                                handleDeleteOptionPress(
                                                                    option.key,
                                                                )
                                                            }
                                                        >
                                                            <DeleteOutlineIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            ),
                                        )}
                                    </Box>
                                    <Divider sx={{ my: 1.5 }} />
                                    <Button
                                        size="small"
                                        startIcon={<AddIcon />}
                                        onClick={handleAddOptionPress}
                                    >
                                        Add option
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </CardContent>
            )}
        </Card>
    );
}
